import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import ChangelogLayout from './ChangelogLayout';
import NewsItem from './NewsItem';
import { useNews } from '../../hooks/useNews';
import useChangelog from '../../hooks/useChangelog';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const renderIcon = (iconName, color = 'primary') => {
  if (MuiIcons[iconName]) {
    const IconComponent = MuiIcons[iconName];
    return <IconComponent color={color} />;
  }
  console.warn(`Icon "${iconName}" not found in Material-UI icons. Using default icon.`);
  const DefaultIcon = MuiIcons.Info || MuiIcons.Help;
  return <DefaultIcon color={color} />;
};

// mode: 'all' (default) shows news + changelog, 'news' or 'changelog' restrict
// the feed to a single source so it can be surfaced as its own dashboard widget.
function NewsAndChangelog({ mode = 'all' }) {
  const { fetchNews, isLoading: newsLoading } = useNews();
  const { changelogData, loading: changelogLoading, getAllEntries } = useChangelog();
  const [news, setNews] = useState([]);

  useEffect(() => {
    const loadNews = async () => {
      try {
        const newsData = await fetchNews();
        setNews(newsData || []);
      } catch (error) {
        console.error('Error loading news:', error);
        setNews([]);
      }
    };

    loadNews();
  }, [fetchNews]);

  const combinedItems = useMemo(() => {
    if (changelogLoading || newsLoading) return [];

    const items = [];

    if (changelogData?.changelog) {
      const changelogEntries = getAllEntries();
      changelogEntries.forEach(entry => {
        items.push({
          type: 'changelog',
          date: new Date(entry.date),
          data: entry,
        });
      });
    }

    news.forEach(newsItem => {
      items.push({
        type: 'news',
        date: new Date(newsItem.date),
        data: newsItem,
      });
    });

    const visible = mode === 'all' ? items : items.filter(item => item.type === mode);
    visible.sort((a, b) => b.date - a.date);
    return visible;
  }, [changelogData, changelogLoading, getAllEntries, news, newsLoading, mode]);

  const emptyMessage =
    mode === 'news'
      ? 'Aktuell keine Neuigkeiten.'
      : mode === 'changelog'
        ? 'Keine Changelog-Einträge vorhanden.'
        : 'Keine News oder Changelogs vorhanden.';

  if (changelogLoading || newsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (combinedItems.length === 0) {
    return (
      <Alert severity='info' sx={{ margin: 2 }}>
        {emptyMessage}
      </Alert>
    );
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {combinedItems.map((item, index) => {
        if (item.type === 'news') {
          return <NewsItem key={`news-${item.data._id}`} news={item.data} />;
        } else {
          const entry = item.data;
          return (
            <ChangelogLayout key={`changelog-${entry.id || index}`} title={entry.title} date={entry.date}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ marginBottom: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 1 }}>
                    <Chip label={`v${entry.version}`} color='primary' size='small' />
                  </Box>
                  {entry.description && (
                    <Typography variant='body2' color='text.secondary'>
                      {entry.description}
                    </Typography>
                  )}
                </Box>

                {entry.categories?.map((category, categoryIndex) => (
                  <Box key={categoryIndex}>
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 600, marginBottom: 1, color: 'primary.main' }}
                    >
                      {category.name}
                    </Typography>
                    <List dense>
                      {category.items?.map((categoryItem, itemIndex) => (
                        <ListItem key={itemIndex}>
                          <ListItemIcon>{renderIcon(categoryItem.icon, category.color)}</ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant='body1' sx={{ fontWeight: 500 }}>
                                {categoryItem.title}
                              </Typography>
                            }
                            secondary={categoryItem.description}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </Box>
            </ChangelogLayout>
          );
        }
      })}
    </Box>
  );
}

export default NewsAndChangelog;

